import { useMutation } from "@tanstack/react-query";
import { fileScanningContract } from "@common/contracts/file-scanning.contract";
import { AccountClass } from "@common/constants/domains/accounting/accounts";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import { getBackendAPIClient } from "@lib/backendAPIClient";

const fileScanningClient = getBackendAPIClient(fileScanningContract);

export function useProcessCoADocument() {
    return useMutation({
        mutationFn: async ({
            entityId,
            documentId,
        }: {
            entityId: number;
            documentId: number;
        }) => {
            const response = await fileScanningClient.processCoADocument({
                params: {
                    entityId,
                    documentId,
                },
            });

            return response.body;
        },
    });
}

export function useExtractBalanceSheet() {
    return useMutation({
        mutationFn: async ({
            entityId,
            documentId,
        }: {
            entityId: number;
            documentId: number;
        }) => {
            const response = await fileScanningClient.extractBalanceSheet({
                params: {
                    entityId,
                    documentId,
                },
            });

            return response.body;
        },
    });
}

export function useMapCoaTypesCsv() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            file,
        }: {
            workspaceId: string;
            file: string;
        }) => {
            const response = await fileScanningClient.mapCoaTypesCsv({
                body: {
                    workspaceId,
                    file,
                },
            });

            if (response.status === 200) {
                return response.body;
            }
            const parsedError = ErrorMessageSchema.safeParse(response.body);
            throw new Error(
                parsedError.success
                    ? parsedError.data.message
                    : "Failed to map CoA types",
            );
        },
    });
}

export function useMapAccountsToCoA() {
    return useMutation({
        mutationFn: async ({
            entityId,
            accountNames,
        }: {
            entityId: number;
            accountNames: {
                [AccountClass.assets]: string[];
                [AccountClass.liabilities]: string[];
                [AccountClass.equity]: string[];
                [AccountClass.income]: string[];
                [AccountClass.expenses]: string[];
            };
        }) => {
            const response = await fileScanningClient.mapAccountsToCoA({
                params: {
                    entityId,
                },
                body: {
                    accountNames,
                },
            });

            return response.body;
        },
    });
}
